import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";

import { useEffect, useState } from "react";
import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    fetchCountry,
    fetchCity,
    fetchOffice,
} from "@/store/slices/internalUsersSlice";
import { Input, Typography } from "@material-tailwind/react";
import { assignRfqToSalesManager, fetchSalesManager, getRfqById, getSalesMemberById, resetStatus } from "@/store/slices/rfqSlice";
import Toaster from "@/components/Toaster";
import { getSettingData } from "@/store/slices/settingSlice";
import moment from "moment";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};
// const schema = yup.object().shape({
//     country_id: yup.string().required("Country is required"),
//     city_id: yup.string().required("City is required"),
//     office_id: yup.string().required("Office is required"),
//     sales_representative_id: yup.string().required("Procurement manager is required"),
// });


export function AssignProcurementModal({isOpen, onClose, rfqId, assigned_details = null }) {
    
    const dispatch = useDispatch();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const schema = yup.object().shape({
    country_id: yup.string().required(t("country_required")),
    city_id: yup.string().required(t("city_required")),
    office_id: yup.string().required(t("office_required")),
    sales_representative_id: yup.string().required(t("procurement_manager_required")),
    });

    const [isAlreadyAssigned, setIsAlreadyAssigned] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            rfq_ids: [rfqId],
        }, resolver: yupResolver(schema)
    });

    const { cities, countries, offices } = useSelector((state) => state.internalUser);
    const { sales_managers = [], sales_team = [],  loading } = useSelector((state) => state.rfq);
   
    useEffect(() => {
        dispatch(fetchCountry());
    }, [dispatch]);

    useEffect(() => {
        if (isAlreadyAssigned) return;

        if (countries && countries.length > 0) {
            const saudiArabia = countries.find(c => c.label === 'Saudi Arabia');
            if (saudiArabia) {
                setValue('country_id', saudiArabia.value, { shouldValidate: true });
                dispatch(fetchCity(saudiArabia.value));
            }
        }
    }, [countries, isAlreadyAssigned, setValue, dispatch]);

    useEffect(() => {
        if (!assigned_details) {
            setIsAlreadyAssigned(false);
            return;
        }

        const procurementData = assigned_details.filter(
            item => item.type === "Procurement"
        );
        
        if (!procurementData.length) {
            setIsAlreadyAssigned(false);
            return;
        }

        const managerData = procurementData.find(
            item => item.designation?.toLowerCase() === "manager"
        );

        const memberData = procurementData.find(
            item => item.designation?.toLowerCase() !== "manager"
        );

        if (managerData || memberData) {
            setIsAlreadyAssigned(true);
        }

        // 1. Country
        if (managerData?.country_id) {
            setValue("country_id", managerData.country_id);
            dispatch(fetchCity(managerData.country_id));
        }

        // 2. City
        if (managerData?.city_id) {
            setValue("city_id", managerData.city_id);
            dispatch(fetchOffice(managerData.city_id));
        }

        // 3. Office
        if (managerData?.office_id) {
            setValue("office_id", managerData.office_id);
            dispatch(fetchSalesManager({
                office_id: managerData.office_id,
                type: "Procurement"
            }));
        }

        // 4. Manager
        if (managerData?.assigned_to_id) {
            setValue("sales_representative_id", managerData.assigned_to_id);

            dispatch(getSalesMemberById({
                parent_id: managerData.assigned_to_id,
                type: "Procurement"
            }));
        }

        // 5. Member (optional)
        if (memberData?.assigned_to_id) {
            setSelectedUsers(memberData.assigned_to_id);
        }

    }, [assigned_details]);

    const onSubmit = async (data) => {
        // Extra safeguard check
        if (isAlreadyAssigned) return;

        try {
            const payload = {
                ...data,
                rfq_ids: [rfqId],
                type:'Procurement',
                sales_members: [selectedUsers], 
                
            };
            const result = await dispatch(assignRfqToSalesManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || `RFQ assigned successfully!`);
                dispatch(resetStatus());
                reset();
                onClose();
                dispatch(getRfqById(rfqId));
            }else if(!result?.status && result?.data?.users_on_leave){
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || "Assign user is on leave!");
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || "Something went wrong!");
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || "Failed to assign RFQ");
            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => { 
            try {
            const payload = {
                ...getValues(),
                sales_members: [selectedUsers],
                force_assign: true, 
            };
            const result = await dispatch(assignRfqToSalesManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || `RFQ assigned successfully!`);
                dispatch(resetStatus());
                reset();
                onClose();
                setLeaveConfirmation(null);
            }else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || "Something went wrong!");
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || "Failed to assign RFQ");
            dispatch(resetStatus());
        }
    }


    const [selectedUsers, setSelectedUsers] = useState(null);

    return (
        <Dialog
            open={isOpen}
            handler={onClose}
            size="md"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {isAlreadyAssigned ? t('view_procurement_title') : t('assign_to_procurement_title')}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 max-h-[65vh] overflow-y-auto">
                    <DialogBody>
                        {isAlreadyAssigned && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm font-medium">
                                {t('procurement_already_assigned_note')}
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">
                            {/* Country selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t('country_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select Country"
                                    options={countries}
                                    value={countries?.find(c => c.value === watch("country_id")) || null}
                                    onChange={(c) => {
                                        setValue("country_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchCity(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    isDisabled={isAlreadyAssigned} 
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{errors.country_id.message}</p>
                                )}
                            </div>

                            {/* City selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t('city_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select City"
                                    options={cities}
                                    value={cities?.find(c => c.value === watch("city_id")) || null}
                                    onChange={(c) => {
                                        setValue("city_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    isDisabled={isAlreadyAssigned} 
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{errors.city_id.message}</p>
                                )}
                            </div>

                            {/* Office selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t('office_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select Office"
                                    options={offices}
                                    value={offices?.find(o => o.value === watch("office_id")) || null}
                                    onChange={(o) => {
                                        setValue("office_id", o?.value, { shouldValidate: true });
                                        if (o?.value) dispatch(fetchSalesManager({office_id:o?.value,type:'Procurement'}));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    isDisabled={isAlreadyAssigned}
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{errors.office_id.message}</p>
                                )}
                            </div>

                            {/* Procurement Manager selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t('procurement_manager_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_procurement_manager_placeholder')}
                                    options={sales_managers}
                                    value={sales_managers?.find(c => c.value === watch("sales_representative_id")) || null}
                                    onChange={(m) => {
                                        setValue("sales_representative_id", m?.value, {
                                            shouldValidate: true,
                                        });
                                        setSelectedUsers(null);
                                        dispatch(getSalesMemberById({parent_id:m?.value, type:'Procurement'}));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    isDisabled={isAlreadyAssigned} 
                                />
                                {errors.sales_representative_id && (
                                    <p className="text-red-500 text-sm">
                                        {errors.sales_representative_id.message}
                                    </p>
                                )}
                            </div>
                            {sales_team && <div className="col-span-2">
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t('procurement_team_members_label')}
                                </Typography>
                                <Select
                                    placeholder={t('select_procurement_member_placeholder')}
                                    options={sales_team}
                                    onChange={(m) => {
                                        setSelectedUsers(m ? m.value : null); 
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => t('no_procurement_member_available')}
                                    value={sales_team?.find(option => option.value === selectedUsers) || null}
                                    isDisabled={isAlreadyAssigned} // Handled state lockout
                                />
                            </div>}
                        </div>
                    </DialogBody>
                </div>
                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
                        {/* Header with Warning Icon */}
                        <div className="flex items-center gap-2 mb-3">
                        <svg
                            className="h-5 w-5 text-amber-600 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                        >
                            <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                            />
                        </svg>
                        <h3 className="font-semibold text-amber-800">
                            {t('schedule_conflict_title')}
                        </h3>
                        </div>
    
                        {/* User List */}
                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                        {leaveConfirmation.map((user) => (
                            <li
                            key={user.user_id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4"
                            >
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-gray-900">{user.name}</span>
                                {(user.user_type || user.role) && (
                                <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                    {[user.user_type, user.role].filter(Boolean).join(" • ")}
                                </span>
                                )}
                            </div>
    
                            <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                {moment(user.leave_from).format("DD MMM YYYY")} –{" "}
                                {moment(user.leave_to).format("DD MMM YYYY")}
                            </div>
                            </li>
                        ))}
                        </ul>
    
                        {/* Actionable Footer Copy */}
                        <p className="text-sm text-amber-800">
                        {t('force_assign_notice')}
                        </p>
                    </div>
                )}

                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {ct('notNowButton')}
                    </button>
                    <button 
                        type="submit" 
                        disabled={isAlreadyAssigned}
                        className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-200 text-gray-900 disabled:text-gray-400 font-medium shadow-sm disabled:shadow-none disabled:cursor-not-allowed transition-all"
                    >
                        {t('assign_now_button')}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                     <button
                        type="button"
                        className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm"
                        onClick={() => handleForceAssign()}
                    >
                        {t('force_assign_button')}
                    </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default AssignProcurementModal;
